import { computed, inject, provide, type ComputedRef, type InjectionKey } from 'vue'
import { useStore } from '@web/store'
import { specialProjectId, superSubscriptionSegment, ffNmsSegment } from '@web/common/ExternalConfig/getters'
import STREAM_STATUS from '@web/consts/StreamStatus'
import type { OnboardingContext } from './types'
import { selectHasSelfieCourse } from './selectors/selectHasSelfieCourse'
import { selectHasFfNmsSegment } from './selectors/selectFfNmsSegment'

// Exported so step-component tests can `provide` the context directly without mounting the host.
export const ONBOARDING_CONTEXT_KEY: InjectionKey<ComputedRef<OnboardingContext>> = Symbol('onboardingContext')

export function provideOnboardingContext (): ComputedRef<OnboardingContext> {
  const store = useStore()

  const ctx = computed<OnboardingContext>(() => {
    const user = store.getters['user/user']
    const isShowNewLk = store.getters['user/getShowNewLk']
    const communities = store.getters['community/communities'] || []
    const streams = store.getters['stream/streams'] || []
    const suggestedUserCourseStream = store.getters['stream/getStreamById'](user.onboarding?.suggestedUserCourseStream || 0)

    const hasSuperSubscription = user.segments?.some((segment: Segment) => superSubscriptionSegment() === segment.id) ?? false
    const firstUserCourseStream = suggestedUserCourseStream || streams
      .sort((a: { createdAt: number }, b: { createdAt: number }) => a.createdAt - b.createdAt)
      .find((stream: { status: string }) => stream.status === STREAM_STATUS.OPEN)
    const firstCourse = firstUserCourseStream
      ? store.getters['course/getCourseById'](firstUserCourseStream.courseId)
      : undefined
    const clubFF = communities.find((c: { uniq: string }) => c.uniq === '1_first_community')
    const hasSelfieCourse = selectHasSelfieCourse(user)
    const hasFfNmsSegment = selectHasFfNmsSegment(user, ffNmsSegment())
    const authOptions = store.getters['authV3/getAuthOptions']

    return {
      user,
      isShowNewLk,
      isTake: specialProjectId() === 'take',
      hasSuperSubscription,
      hasSelfieCourse,
      hasFfNmsSegment,
      clubFFExist: true,
      clubFF,
      firstCourse,
      firstUserCourseStream,
      firstName: user?.firstName || '',
      smsAvailable: authOptions?.smsAvailable ?? false,
      phoneCountry: store.getters['authV3/phoneCountry'] ?? ''
    }
  })

  provide(ONBOARDING_CONTEXT_KEY, ctx)
  return ctx
}

export function useOnboardingContext (): ComputedRef<OnboardingContext> {
  const injected = inject(ONBOARDING_CONTEXT_KEY, null)
  if (injected) return injected
  // Fallback for tests / standalone usage: build a fresh context.
  // In production the host (OnboardingHost) is the only provider — children always inject.
  return provideOnboardingContext()
}
